import { UTCDate } from '@date-fns/utc';
import { ColumnDef } from '@tanstack/react-table';
import { format, subMonths } from 'date-fns';
import { Info, InfoIcon } from 'lucide-react';
import { AmountDisplay } from '../../../../design-system/amount-display/amount-display';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Tooltip } from '../../../../design-system/tooltip/tooltip';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { formatMomChange } from '../../utils/calculate-monthly-breakdown';
import { ReportingData } from '../../utils/get-reporting-data';
import { ExpandableNameCell } from './expandable-name-cell';
import { ExpansionChevronIconButton } from './expansion-chevron-icon-button';

const today = new UTCDate();

export const reportingTableColumnsDefinition: ColumnDef<ReportingData>[] = [
  {
    accessorKey: 'name',
    header: ({ table }) => {
      return (
        <Inline space={3}>
          <ExpansionChevronIconButton
            isExpanded={table.getIsAllRowsExpanded()}
            onClick={table.getToggleAllRowsExpandedHandler()}
          />

          <p>Category</p>
        </Inline>
      );
    },
    cell: ({ row }) => (
      // Min width to prevent layout shift when user toggles row visibility
      <Inline wrap="nowrap">
        <ExpandableNameCell row={row} />

        {row.original.hierarchyLevel === 'VENDOR' && row.original.description && (
          <Tooltip
            trigger={<Info className="h-3 w-3 cursor-pointer" />}
            content={row.original.description}
            contentClassName="text-xs px-3 py-2 w-52"
          />
        )}
      </Inline>
    ),
    footer: 'Grand Total',
    size: 24,
  },
  {
    id: 'actions',
    cell: ({ row, table }) => {
      const entityType = row.original.hierarchyLevel;

      const rowId = row.original.id;
      const parentRowId = row.getParentRow()?.id;

      if (!rowId || !parentRowId) {
        return <div className="w-24"></div>;
      }

      switch (entityType) {
        case 'NO_CATEGORY':
        case 'LEVEL_1_CATEGORY':
          // Keeps the space in rows without the button so there is no layout shift when user toggles row visibility
          return <div className="w-24"></div>;
        case 'LEVEL_2_CATEGORY':
          return (
            <Inline justify="center" className="w-24 opacity-0 transition-opacity group-hover/row:opacity-100">
              <Button
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  table.options.meta?.onMoveCategory?.({
                    id: rowId,
                    parentRowId,
                    level1: row.getParentRow()?.original.name,
                  });
                }}
                className="py-0"
              >
                Move
              </Button>
            </Inline>
          );
        case 'VENDOR':
          return (
            <Inline justify="center" className="w-24 opacity-0 transition-opacity group-hover/row:opacity-100">
              <Button
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  table.options.meta?.onMoveVendor?.({ vendorId: rowId, parentRowId });
                }}
                className="py-0"
              >
                Move
              </Button>
            </Inline>
          );
        default:
          return exhaustiveCheck(entityType);
      }
    },
    size: 10,
    minSize: 10,
  },
  {
    accessorKey: 'three_months_ago_amount',
    accessorFn: (row) => row.amountBreakdown.threeMonthsAgoAmount,
    // Displays name of month -3 in the column header
    header: () => <p>{format(subMonths(today, 3), 'MMM')}</p>,
    cell: ({ row }) => (
      <AmountDisplay
        normalizedAmount={row.original.amountBreakdown.threeMonthsAgoAmount}
        currency={row.original.amountBreakdown.currency}
        className="min-w-24"
      />
    ),
    footer: ({ table }) => (
      <AmountDisplay
        normalizedAmount={table.options.meta?.totals?.threeMonthsAgoAmount ?? 0}
        currency={table.options.meta?.totals?.currency}
        className="min-w-24"
      />
    ),
    size: 11,
    minSize: 11,
  },
  {
    accessorKey: 'two_months_ago_amount',
    accessorFn: (row) => row.amountBreakdown.twoMonthsAgoAmount,
    // Displays name of month -2 in the column header
    header: () => format(subMonths(today, 2), 'MMM'),
    cell: ({ row }) => (
      <AmountDisplay
        normalizedAmount={row.original.amountBreakdown.twoMonthsAgoAmount}
        currency={row.original.amountBreakdown.currency}
        className="min-w-24"
      />
    ),
    footer: ({ table }) => (
      <AmountDisplay
        normalizedAmount={table.options.meta?.totals?.twoMonthsAgoAmount ?? 0}
        currency={table.options.meta?.totals?.currency}
        className="min-w-24"
      />
    ),
    size: 11,
    minSize: 11,
  },
  {
    accessorKey: 'previous_month_amount',
    accessorFn: (row) => row.amountBreakdown.previousMonthAmount,
    // Displays name of month -1 in the column header
    header: () => format(subMonths(today, 1), 'MMM'),
    cell: ({ row }) => (
      <AmountDisplay
        normalizedAmount={row.original.amountBreakdown.previousMonthAmount}
        currency={row.original.amountBreakdown.currency}
        className="min-w-24"
      />
    ),
    footer: ({ table }) => (
      <AmountDisplay
        normalizedAmount={table.options.meta?.totals?.previousMonthAmount ?? 0}
        currency={table.options.meta?.totals?.currency}
        className="min-w-24"
      />
    ),
    size: 11,
    minSize: 11,
  },
  {
    id: 'current_month_amount',
    accessorFn: (row) => row.amountBreakdown.currentMonthAmount,
    // Displays name of the current month in the column header
    header: () => format(today, 'MMM'),
    cell: ({ row }) => (
      <AmountDisplay
        normalizedAmount={row.original.amountBreakdown.currentMonthAmount}
        currency={row.original.amountBreakdown.currency}
        className="min-w-24"
      />
    ),
    footer: ({ table }) => (
      <AmountDisplay
        normalizedAmount={table.options.meta?.totals?.currentMonthAmount ?? 0}
        currency={table.options.meta?.totals?.currency}
        className="min-w-24"
      />
    ),
    size: 11,
    minSize: 11,
  },
  {
    accessorKey: 'totalAmount',
    header: () => 'Total',
    cell: ({ row }) => (
      <AmountDisplay
        normalizedAmount={row.original.amountBreakdown.totalAmount}
        currency={row.original.amountBreakdown.currency}
        className="min-w-24"
      />
    ),
    footer: ({ table }) => (
      <AmountDisplay
        normalizedAmount={table.options.meta?.totals?.totalAmount ?? 0}
        currency={table.options.meta?.totals?.currency}
        className="min-w-24"
      />
    ),
    size: 11,
    minSize: 11,
  },
  {
    accessorKey: 'month_over_month_increase',
    accessorFn: (row) => row.amountBreakdown.monthOverMonthIncrease,
    header: () => (
      <Tooltip
        trigger={
          <Inline className="cursor-help" space={1} justify="center">
            <p>MoM</p> <InfoIcon className="h-3 w-3" />
          </Inline>
        }
        content={`% change, ${format(subMonths(today, 1), 'MMMM')} vs ${format(subMonths(today, 2), 'MMMM')}`}
      />
    ),
    cell: ({ row }) => {
      const momData = formatMomChange(
        row.original.amountBreakdown.monthOverMonthIncrease,
        row.original.amountBreakdown.twoMonthsAgoAmount,
      );

      const ArrowIcon = momData.arrow;
      return (
        <div className={`min-w-24 text-right ${momData.colorClass}`}>
          <span className="flex items-center justify-end gap-1">
            {momData.sign}
            {momData.percentageChange}% <ArrowIcon size={16} strokeWidth={2} />
          </span>
        </div>
      );
    },
    footer: ({ table }) => {
      const momData = formatMomChange(
        table.options.meta?.totals?.monthOverMonthIncrease ?? 0,
        table.options.meta?.totals?.twoMonthsAgoAmount ?? 0,
      );

      const ArrowIcon = momData.arrow;
      return (
        <div className={`min-w-24 text-right ${momData.colorClass}`}>
          <span className="flex items-center justify-end gap-1">
            {momData.sign}
            {momData.percentageChange}% <ArrowIcon size={16} strokeWidth={2} />
          </span>
        </div>
      );
    },
    size: 11,
    minSize: 11,
  },
];
